import { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { flushSync } from 'react-dom';
import { PageHeader, type TableActionItem } from '@/components/shared';
import { useBackofficePageTitle } from '@/lib/page-title';
import { usePersistedSearchParams } from '@/preferences';
import {
  checkBackofficeLinkSlugAvailability,
  createBackofficeLink,
  fetchBackofficeLinks,
  searchBackofficeStudies,
} from './api';
import { LinkEditorModal, LinksFilters, LinksTable } from './components';
import type { BackofficeLink, BackofficeStudyOption } from './types';

function createSlugCandidate(): string {
  const randomPart = Math.random().toString(36).slice(2, 8);
  const timePart = Date.now().toString(36).slice(-4);
  return `${timePart}-${randomPart}`;
}

export function BackofficeLinksPage() {
  useBackofficePageTitle('Links');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [links, setLinks] = useState<BackofficeLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const requestParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set('status', searchParams.get('status') ?? 'active');
    params.set('targetType', searchParams.get('targetType') ?? 'all');
    const query = searchParams.get('query');
    if (query) params.set('query', query);
    if (params.get('status') === 'all' || params.get('status') === 'archived') {
      params.set('includeArchived', 'true');
    }
    return params;
  }, [searchParams]);

  const status = searchParams.get('status') ?? 'active';
  const targetType = searchParams.get('targetType') ?? 'all';
  const query = searchParams.get('query') ?? '';

  const loadLinks = useCallback(
    async (signal?: AbortSignal) => {
      const response = await fetchBackofficeLinks(new URLSearchParams(requestParams), signal);
      setLinks(response.links);
    },
    [requestParams]
  );

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    loadLinks(controller.signal)
      .catch((caught) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setError(caught instanceof Error ? caught.message : 'Failed to load links');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [loadLinks]);

  usePersistedSearchParams({
    scope: 'list:links',
    keys: ['status', 'targetType', 'query'],
    searchParams,
    setSearchParams,
  });

  async function handleSearchStudies(query: string): Promise<BackofficeStudyOption[]> {
    const response = await searchBackofficeStudies(query);
    return response.studies;
  }

  async function checkSlugAvailability(slug: string): Promise<boolean> {
    const response = await checkBackofficeLinkSlugAvailability(slug);
    return response.available;
  }

  async function generateUniqueSlug(): Promise<string> {
    for (let attempt = 0; attempt < 12; attempt += 1) {
      const candidate = createSlugCandidate();
      if (await checkSlugAvailability(candidate)) {
        return candidate;
      }
    }
    throw new Error('Failed to generate a unique slug');
  }

  async function refresh() {
    setError(null);
    await loadLinks();
  }

  const tableActions = useMemo<TableActionItem[]>(
    () => [
      {
        id: 'refresh',
        label: 'Refresh',
        loadingLabel: 'Refreshing',
        icon: RefreshCw,
        onSelect: () => {
          void refresh().catch((caught) => {
            setError(caught instanceof Error ? caught.message : 'Failed to refresh links');
          });
        },
        placement: 'fixed-inline',
        disabled: loading,
        isLoading: false,
      },
    ],
    [loading]
  );

  async function handleSubmit(input: { slug?: string; target?: unknown; description?: string }) {
    setSubmitting(true);
    setSubmitError(null);
    try {
      if (input.slug && input.target) {
        const response = await createBackofficeLink({
          slug: input.slug,
          target: input.target as never,
          ...(input.description ? { description: input.description } : {}),
        });
        flushSync(() => {
          setCreateModalOpen(false);
        });
        void refresh().catch((caught) => {
          setError(caught instanceof Error ? caught.message : 'Failed to refresh links');
        });
        navigate(`/links/${response.link.slug}`);
        return;
      }
    } catch (caught) {
      setSubmitError(caught instanceof Error ? caught.message : 'Failed to save link');
    } finally {
      setSubmitting(false);
    }
  }

  function updateParam(key: string, value: string) {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (!value) next.delete(key);
      else next.set(key, value);
      return next;
    });
  }

  return (
    <main className="min-h-[320px] bg-white p-6 md:p-8" aria-label="Backoffice main content">
      <div className="flex flex-col gap-5">
        <PageHeader
          actions={
            <button
              type="button"
              onClick={() => {
                setSubmitError(null);
                setCreateModalOpen(true);
              }}
              className="focus-ring inline-flex h-10 items-center whitespace-nowrap rounded-lg border border-backoffice-500 bg-backoffice-500 px-4 text-small font-medium text-white hover:bg-backoffice-600"
            >
              Create link
            </button>
          }
          showShareButton={false}
        >
          Links
        </PageHeader>

        <section className="rounded-xl border border-neutral-200 bg-white">
          <LinksFilters
            query={query}
            status={status}
            targetType={targetType}
            actions={tableActions}
            onQueryChange={(value: string) => updateParam('query', value.trim())}
            onStatusChange={(value: string) => updateParam('status', value)}
            onTargetTypeChange={(value: string) => updateParam('targetType', value)}
          />

          {loading ? (
            <div className="px-5 py-16 text-center text-body text-neutral-500">Loading links</div>
          ) : error ? (
            <div className="px-5 py-16 text-center text-body text-red-700">{error}</div>
          ) : links.length > 0 ? (
            <LinksTable
              links={links}
              onSelectLink={(link: BackofficeLink) => {
                navigate(`/links/${link.slug}`);
              }}
            />
          ) : (
            <div className="px-5 py-16 text-center text-body text-neutral-500">No links found</div>
          )}
        </section>

        {createModalOpen ? (
          <LinkEditorModal
            mode="create"
            link={null}
            submitting={submitting}
            error={submitError}
            onClose={() => {
              if (!submitting) {
                setCreateModalOpen(false);
              }
            }}
            onCheckSlugAvailability={checkSlugAvailability}
            onGenerateUniqueSlug={generateUniqueSlug}
            onSearchStudies={handleSearchStudies}
            onSubmit={handleSubmit}
          />
        ) : null}
      </div>
    </main>
  );
}
